import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { apiClient } from "../services/apiClient";

const EditableDocumentContext = createContext();

export const useEditableDocument = () => {
  const context = useContext(EditableDocumentContext);
  if (!context) {
    throw new Error(
      "useEditableDocument must be used within an EditableDocumentProvider"
    );
  }
  return context;
};

export const EditableDocumentProvider = ({ children }) => {
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [error, setError] = useState("");

  const clearError = useCallback(() => {
    setError("");
  }, []);

  // Fetch document templates
  const fetchTemplates = useCallback(async () => {
    try {
      setTemplatesLoading(true);
      setError("");

      const response = await apiClient.get("/api/editable-documents/templates");

      if (response.success) {
        setTemplates(response.data.templates || []);
      } else {
        throw new Error(response.message || "Failed to fetch templates");
      }
    } catch (err) {
      console.error("Error fetching templates:", err);
      setError(err.message);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Fetch documents for a proposal
  const fetchDocuments = useCallback(async (proposalId) => {
    try {
      setLoading(true);
      setError("");

      const response = await apiClient.get(
        `/api/editable-documents/proposal/${proposalId}`
      );

      if (response.success) {
        setDocuments(response.data.documents || []);
        return {
          success: true,
          data: response.data.documents,
        };
      } else {
        throw new Error(response.message || "Failed to fetch documents");
      }
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError(err.message);
      return {
        success: false,
        error: err.message,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch a specific document with sections
  const fetchDocument = useCallback(async (documentId) => {
    try {
      setLoading(true);
      setError("");

      const response = await apiClient.get(
        `/api/editable-documents/${documentId}`
      );

      if (response.success) {
        setCurrentDocument(response.data.document);
        return {
          success: true,
          data: response.data.document,
        };
      } else {
        throw new Error(response.message || "Failed to fetch document");
      }
    } catch (err) {
      console.error("Error fetching document:", err);
      setError(err.message);
      setCurrentDocument(null);
      return {
        success: false,
        error: err.message,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new document
  const createDocument = useCallback(
    async (proposalId, templateId, documentName, options = {}) => {
      try {
        setLoading(true);
        setError("");

        const requestData = {
          proposalId,
          templateId,
          documentName,
          generateInitialContent: options.generateInitialContent || false,
          sourceDocuments: options.sourceDocuments || [],
        };

        const response = await apiClient.post(
          "/api/editable-documents",
          requestData
        );

        if (response.success) {
          // Refresh documents list if we have documents loaded
          if (documents.length > 0) {
            await fetchDocuments(proposalId);
          }

          return {
            success: true,
            data: response.data.document,
          };
        } else {
          throw new Error(response.message || "Failed to create document");
        }
      } catch (err) {
        console.error("Error creating document:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [documents.length, fetchDocuments]
  );

  // Update document metadata
  const updateDocument = useCallback(
    async (documentId, updates) => {
      try {
        setLoading(true);
        setError("");

        const response = await apiClient.put(
          `/api/editable-documents/${documentId}`,
          updates
        );

        if (response.success) {
          // Update current document if it's the one being updated
          if (currentDocument && currentDocument.document_id === documentId) {
            setCurrentDocument(response.data.document);
          }

          // Update documents list
          setDocuments((prev) =>
            prev.map((doc) =>
              doc.document_id === documentId
                ? { ...doc, ...updates, updated_at: new Date().toISOString() }
                : doc
            )
          );

          return {
            success: true,
            data: response.data.document,
          };
        } else {
          throw new Error(response.message || "Failed to update document");
        }
      } catch (err) {
        console.error("Error updating document:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [currentDocument]
  );

  // Delete a document
  const deleteDocument = useCallback(
    async (documentId) => {
      try {
        setLoading(true);
        setError("");

        const response = await apiClient.delete(
          `/api/editable-documents/${documentId}`
        );

        if (response.success) {
          // Clear current document if it's the one being deleted
          if (currentDocument && currentDocument.document_id === documentId) {
            setCurrentDocument(null);
          }

          // Remove from documents list
          setDocuments((prev) =>
            prev.filter((doc) => doc.document_id !== documentId)
          );

          return {
            success: true,
          };
        } else {
          throw new Error(response.message || "Failed to delete document");
        }
      } catch (err) {
        console.error("Error deleting document:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [currentDocument]
  );

  // Add a section to a document
  const addSection = useCallback(
    async (documentId, sectionTitle, insertPosition, content = "") => {
      try {
        setError("");

        const response = await apiClient.post(
          `/api/editable-documents/${documentId}/sections`,
          {
            sectionTitle,
            insertPosition,
            content,
          }
        );

        if (response.success) {
          // Refresh current document if it's the one being updated
          if (currentDocument && currentDocument.document_id === documentId) {
            await fetchDocument(documentId);
          }

          return {
            success: true,
            data: { sectionId: response.data.sectionId },
          };
        } else {
          throw new Error(response.message || "Failed to add section");
        }
      } catch (err) {
        console.error("Error adding section:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      }
    },
    [currentDocument, fetchDocument]
  );

  // Update a section
  const updateSection = useCallback(
    async (sectionId, updates) => {
      try {
        setError("");

        const response = await apiClient.put(
          `/api/editable-documents/sections/${sectionId}`,
          updates
        );

        if (response.success) {
          // Update current document's sections
          if (currentDocument) {
            setCurrentDocument((prev) => ({
              ...prev,
              sections: prev.sections.map((section) =>
                section.section_id === sectionId
                  ? {
                      ...section,
                      ...updates,
                      updated_at: new Date().toISOString(),
                      word_count: updates.content
                        ? updates.content.split(" ").length
                        : section.word_count,
                    }
                  : section
              ),
              updated_at: new Date().toISOString(),
            }));
          }

          return {
            success: true,
          };
        } else {
          throw new Error(response.message || "Failed to update section");
        }
      } catch (err) {
        console.error("Error updating section:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      }
    },
    [currentDocument]
  );

  // Delete a section
  const deleteSection = useCallback(
    async (sectionId) => {
      try {
        setError("");

        const response = await apiClient.delete(
          `/api/editable-documents/sections/${sectionId}`
        );

        if (response.success) {
          // Remove section from current document and reorder
          if (currentDocument) {
            const updatedSections = currentDocument.sections
              .filter((section) => section.section_id !== sectionId)
              .map((section, index) => ({
                ...section,
                section_order: index + 1,
              }));

            setCurrentDocument((prev) => ({
              ...prev,
              sections: updatedSections,
              updated_at: new Date().toISOString(),
            }));
          }

          return {
            success: true,
          };
        } else {
          throw new Error(response.message || "Failed to delete section");
        }
      } catch (err) {
        console.error("Error deleting section:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      }
    },
    [currentDocument]
  );

  // Reorder sections
  const reorderSections = useCallback(
    async (documentId, sectionOrderMap) => {
      try {
        setError("");

        const response = await apiClient.put(
          `/api/editable-documents/${documentId}/sections/reorder`,
          { sectionOrderMap }
        );

        if (response.success) {
          // Update current document's section order
          if (currentDocument && currentDocument.document_id === documentId) {
            const reorderedSections = [...currentDocument.sections].sort(
              (a, b) => {
                const orderA = sectionOrderMap[a.section_id] || a.section_order;
                const orderB = sectionOrderMap[b.section_id] || b.section_order;
                return orderA - orderB;
              }
            );

            setCurrentDocument((prev) => ({
              ...prev,
              sections: reorderedSections,
              updated_at: new Date().toISOString(),
            }));
          }

          return {
            success: true,
          };
        } else {
          throw new Error(response.message || "Failed to reorder sections");
        }
      } catch (err) {
        console.error("Error reordering sections:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      }
    },
    [currentDocument]
  );

  // Generate AI content for section (placeholder for now)
  const generateSectionContent = useCallback(
    async (sectionId, operation, options = {}) => {
      try {
        setError("");

        const response = await apiClient.post(
          `/api/editable-documents/sections/${sectionId}/generate`,
          {
            operation,
            customPrompt: options.customPrompt,
            sourceDocuments: options.sourceDocuments || [],
          }
        );

        if (response.success) {
          return {
            success: true,
            data: response.data,
          };
        } else {
          throw new Error(response.message || "Failed to generate content");
        }
      } catch (err) {
        console.error("Error generating section content:", err);
        setError(err.message);
        return {
          success: false,
          error: err.message,
        };
      }
    },
    []
  );

  // Clear current document
  const clearCurrentDocument = useCallback(() => {
    setCurrentDocument(null);
  }, []);

  // Get document statistics
  const getDocumentStats = useCallback(() => {
    if (!documents.length) return null;

    const stats = {
      total: documents.length,
      draft: documents.filter((d) => d.status === "Draft").length,
      inProgress: documents.filter((d) => d.status === "In_Progress").length,
      review: documents.filter((d) => d.status === "Review").length,
      final: documents.filter((d) => d.status === "Final").length,
      totalWords: documents.reduce(
        (sum, d) => sum + (d.total_word_count || 0),
        0
      ),
      aiGenerated: documents.filter((d) => d.is_ai_generated).length,
    };

    return stats;
  }, [documents]);

  const value = {
    // State
    templates,
    documents,
    currentDocument,
    loading,
    templatesLoading,
    error,

    // Actions
    fetchTemplates,
    fetchDocuments,
    fetchDocument,
    createDocument,
    updateDocument,
    deleteDocument,
    addSection,
    updateSection,
    deleteSection,
    reorderSections,
    generateSectionContent,
    clearCurrentDocument,
    clearError,

    // Computed
    getDocumentStats,

    // Utils
    hasCurrentDocument: !!currentDocument,
    currentDocumentSectionCount: currentDocument?.sections?.length || 0,
    documentCount: documents.length,
  };

  return (
    <EditableDocumentContext.Provider value={value}>
      {children}
    </EditableDocumentContext.Provider>
  );
};
